import { useEffect, useRef, useState } from 'react';
import { LuArrowLeft } from 'react-icons/lu';
import { Alert, Button, Spinner } from 'reactstrap';

import { formatMoney } from 'src/shared/utils/money';
import { formatDuration } from '../calendarUtils';
import BookingSummary from './BookingSummary';
import ClientPicker from './ClientPicker';
import ServicePicker from './ServicePicker';
import WhenPicker from './WhenPicker';
import type { BookingForm } from './useBookingForm';

const STEPS = ['Clienta', 'Servicios', 'Día y hora', 'Confirmar'] as const;

interface BookingWizardProps {
  form: BookingForm;
  titleId: string;
  onClose: () => void;
  onBook: () => void;
  timeZone?: string;
  currency?: string;
  mayOverride: boolean;
}

/**
 * The phone's booking flow: one question per screen, the next step under the
 * thumb.
 *
 * Tapping a client or a free time moves on by itself — those are answers, not
 * selections to confirm — so the common case stays at five taps: client,
 * service, "Continuar", time, "Agendar". Back steps back instead of losing
 * everything.
 */
export default function BookingWizard({
  form,
  titleId,
  onClose,
  onBook,
  timeZone,
  currency,
  mayOverride,
}: BookingWizardProps) {
  const [step, setStep] = useState(() => (form.client || form.isLoadingPreselected ? 1 : 0));
  const heading = useRef<HTMLHeadingElement>(null);
  const firstRender = useRef(true);

  // Each step announces itself: focus lands on its heading, so a screen reader
  // reads the new question and the page does not stay scrolled halfway down.
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    heading.current?.focus();
  }, [step]);

  const back = () => setStep((current) => Math.max(0, current - 1));

  let footer = null;
  if (step === 0 && form.client) {
    footer = (
      <Button color="primary" onClick={() => setStep(1)}>
        Continuar
      </Button>
    );
  } else if (step === 1) {
    footer = (
      <Button color="primary" onClick={() => setStep(2)} disabled={form.serviceIds.length === 0}>
        {form.serviceIds.length === 0
          ? 'Elige al menos un servicio'
          : `Continuar · ${formatDuration(form.totalMinutes)} · ${formatMoney(form.totalPrice, currency)}`}
      </Button>
    );
  } else if (step === 2) {
    footer = (
      <Button color="primary" onClick={() => setStep(3)} disabled={form.startsAt === ''}>
        {form.startsAt === '' ? 'Elige un horario' : 'Continuar'}
      </Button>
    );
  } else if (step === 3) {
    footer = (
      <Button color="primary" onClick={onBook} disabled={!form.canBook || form.create.isPending}>
        {form.create.isPending ? (
          <>
            <Spinner size="sm" /> Agendando…
          </>
        ) : (
          'Agendar cita'
        )}
      </Button>
    );
  }

  return (
    <>
      <div className="modal-header v-wizard__header">
        {step > 0 ? (
          <Button
            color="secondary"
            className="v-icon-btn v-ghost"
            onClick={back}
            aria-label="Paso anterior"
          >
            <LuArrowLeft aria-hidden="true" />
          </Button>
        ) : (
          <span className="v-wizard__spacer" aria-hidden="true" />
        )}
        <div className="v-wizard__titles">
          <h2 className="modal-title" id={titleId}>
            Nueva cita
          </h2>
          <span className="v-wizard__step">
            Paso {step + 1} de {STEPS.length} · {STEPS[step]}
          </span>
        </div>
        <button type="button" className="btn-close" aria-label="Cerrar" onClick={onClose} />
      </div>
      <div className="v-wizard__progress" aria-hidden="true">
        {STEPS.map((label, index) => (
          <span key={label} className={index <= step ? 'is-done' : undefined} />
        ))}
      </div>

      <div className="modal-body">
        <h3 ref={heading} tabIndex={-1} className="v-wizard__question">
          {
            [
              '¿Para quién es la cita?',
              '¿Qué se va a hacer?',
              '¿Qué día y a qué hora?',
              'Revisa y agenda',
            ][step]
          }
        </h3>

        {step === 0 && <ClientPicker form={form} onPicked={() => setStep(1)} />}
        {step === 1 && <ServicePicker form={form} currency={currency} />}
        {step === 2 && (
          <WhenPicker
            form={form}
            timeZone={timeZone}
            mayOverride={mayOverride}
            onPicked={() => setStep(3)}
          />
        )}
        {step === 3 && <BookingSummary form={form} timeZone={timeZone} currency={currency} />}
      </div>

      {(footer || form.apiMessage) && (
        <div className="modal-footer v-wizard__footer">
          {form.apiMessage && (
            <Alert color="danger" role="alert" className="w-100 mb-2">
              {form.apiMessage}
            </Alert>
          )}
          {footer}
        </div>
      )}
    </>
  );
}
